import { useState } from "react";
import { createPortal } from "react-dom";
import { useSignal } from "../net/signal";
import { avatarSrc } from "../game/avatar";
import { faNum } from "../game/cards";
import { muted, toggleMute } from "../game/chat";
import {
  NOTE_MAX, REPORT_REASONS, blockList, blockPlayer, blocked, currentGame, reportPlayer, safetyError, unblockPlayer,
  type ReportReason,
} from "../net/safety";
import { Overlay } from "./Overlay";
import { notify } from "./Dialog";
import { CountryFlag } from "./CountryFlag";

/**
 * برگه‌ی بازیکن — از روی پیامِ چت یا صندلیِ اتاق (docs/report-block.md).
 *
 * سه کار با سه شدت، به همین ترتیب، تا سبک‌ترین دمِ دست باشد:
 *   - «بی‌صدا برای من»: فقط چت، فقط همین مرورگر؛ خودش خبر ندارد.
 *   - «بلاک»: روی حساب و همه‌ی دستگاه‌ها؛ پیام و صدایش نمی‌رسد و در اتاقِ تو نمی‌نشیند.
 *   - «گزارش»: برای ادمین، با چند پیامِ آخرش. او نمی‌فهمد چه کسی گزارش داده.
 */
export type SheetPlayer = { id: string; name: string; avatar?: string; country?: string; spectator?: boolean };

export function PlayerSheet({ player, context, onClose }: {
  player: SheetPlayer;
  context: "chat" | "seat" | "room" | "watch";
  onClose: () => void;
}) {
  const [view, setView] = useState<"menu" | "report" | "done">("menu");
  const [done, setDone] = useState<{ duplicate: boolean; blocked: boolean }>({ duplicate: false, blocked: false });
  const name = player.name || "بازیکن";

  // پورتال به body: برگه از روی پنجره‌ی چت یا اتاق باز می‌شود و نباید در لایه‌بندیِ آن گیر کند.
  return createPortal(
    <Overlay label={`بازیکن: ${name}`} base="veil" className="psheetwrap" onClose={onClose}>
      <div className="box psheet">
        <div className="pshead">
          <span className="cflag-host"><img className="psface" src={avatarSrc(player.avatar)} alt="" width={64} height={64} /><CountryFlag code={player.country} /></span>
          <div className="stack">
            <b className="psname">{name}</b>
            {player.spectator && <span className="muted">تماشاچی</span>}
          </div>
        </div>
        {view === "menu" && <Menu player={player} onReport={() => setView("report")} onClose={onClose} />}
        {view === "report" && (
          <ReportForm
            player={player}
            context={context}
            onBack={() => setView("menu")}
            onSent={(r) => { setDone(r); setView("done"); }}
          />
        )}
        {view === "done" && <Done name={name} result={done} onClose={onClose} />}
      </div>
    </Overlay>,
    document.body,
  );
}

function Menu({ player, onReport, onClose }: { player: SheetPlayer; onReport: () => void; onClose: () => void }) {
  const mutedSet = useSignal(muted);
  const blockedSet = useSignal(blocked);
  const [busy, setBusy] = useState(false);
  const isMuted = mutedSet.has(player.id);
  const isBlocked = blockedSet.has(player.id);

  const toggleBlock = async () => {
    if (busy) return;
    setBusy(true);
    const r = isBlocked ? await unblockPlayer(player.id) : await blockPlayer(player.id);
    setBusy(false);
    if (!r.ok) { notify(safetyError(r.error ?? "")); return; }
    notify(isBlocked ? "بلاک برداشته شد" : "بلاک شد؛ پیام و صدایش دیگر به تو نمی‌رسد");
  };

  return (
    <div className="psactions">
      <button className="psrow" onClick={() => toggleMute(player.id)} disabled={isBlocked}>
        <span className="psicon mute" aria-hidden="true"><IconMute /></span>
        <span className="stack">
          <b>{isMuted ? "دوباره ببینمش" : "بی‌صدا برای من"}</b>
          <span className="muted">فقط پیام‌هایش را روی همین دستگاه نمی‌بینی؛ خودش خبر ندارد.</span>
        </span>
      </button>
      <button className={`psrow${isBlocked ? " on" : ""}`} onClick={() => void toggleBlock()} disabled={busy}>
        <span className="psicon block" aria-hidden="true"><IconBlock /></span>
        <span className="stack">
          <b>{isBlocked ? "برداشتن بلاک" : "بلاک"}</b>
          <span className="muted">
            {isBlocked
              ? "بلاکش کرده‌ای: پیام و صدایش به تو نمی‌رسد و در اتاق‌هایت نمی‌نشیند."
              : "پیام و صدایش به تو نمی‌رسد و در اتاقی که تو می‌سازی نمی‌نشیند؛ روی همه‌ی دستگاه‌هایت."}
          </span>
        </span>
      </button>
      <button className="psrow danger" onClick={onReport}>
        <span className="psicon flag" aria-hidden="true"><IconFlag /></span>
        <span className="stack">
          <b>گزارش به ادمین</b>
          <span className="muted">فحش، تقلب، نام یا عکس نامناسب… ادمین بررسی می‌کند.</span>
        </span>
      </button>
      <button className="btn ghost" onClick={onClose}>بستن</button>
    </div>
  );
}

function ReportForm({ player, context, onBack, onSent }: {
  player: SheetPlayer;
  context: "chat" | "seat" | "room" | "watch";
  onBack: () => void;
  onSent: (r: { duplicate: boolean; blocked: boolean }) => void;
}) {
  const blockedSet = useSignal(blocked);
  const already = blockedSet.has(player.id);
  const [reason, setReason] = useState<ReportReason | "">("");
  const [note, setNote] = useState("");
  const [alsoBlock, setAlsoBlock] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const send = async () => {
    if (!reason || busy) return;
    setBusy(true);
    setError("");
    const r = await reportPlayer({
      target: player.id, reason, note: note.trim(), game: currentGame(), context, block: alsoBlock && !already,
    });
    setBusy(false);
    if (!r.ok) { setError(safetyError(r.error)); return; }
    onSent({ duplicate: r.duplicate, blocked: r.blocked });
  };

  return (
    <div className="psreport stack">
      <b className="pstitle">چه شده؟</b>
      <div className="psreasons" role="radiogroup" aria-label="دلیل گزارش">
        {REPORT_REASONS.filter((x) => !x.mafia || currentGame() === "mafia").map((x) => (
          <button
            key={x.key}
            role="radio"
            aria-checked={reason === x.key}
            className={`psreason${reason === x.key ? " on" : ""}`}
            onClick={() => setReason(x.key)}
          >
            <b>{x.fa}</b>
            {x.hint && <span className="muted">{x.hint}</span>}
          </button>
        ))}
      </div>
      <label className="psnote">
        <textarea
          value={note}
          maxLength={NOTE_MAX}
          rows={3}
          placeholder="اگر چیزی می‌خواهی اضافه کنی (اختیاری)"
          onChange={(e) => setNote(e.target.value)}
        />
        <span className="muted">{faNum(note.length)} / {faNum(NOTE_MAX)}</span>
      </label>
      {!already && (
        <label className="row pscheck">
          <input type="checkbox" checked={alsoBlock} onChange={(e) => setAlsoBlock(e.target.checked)} />
          <span>بلاکش هم بکن</span>
        </label>
      )}
      <p className="muted psfine">
        چند پیام آخرش در چت نیم‌ساعت اخیر هم با گزارش می‌رود تا ادمین ببیند. او نمی‌فهمد چه کسی گزارش داده.
      </p>
      {error && <div className="pserr">{error}</div>}
      <button className="btn danger" disabled={!reason || busy} onClick={() => void send()}>
        {busy ? "می‌فرستم…" : "بفرست"}
      </button>
      <button className="btn ghost" disabled={busy} onClick={onBack}>برگشت</button>
    </div>
  );
}

function Done({ name, result, onClose }: { name: string; result: { duplicate: boolean; blocked: boolean }; onClose: () => void }) {
  return (
    <div className="psdone stack">
      <span className="psok" aria-hidden="true"><IconCheck /></span>
      <b className="pstitle">{result.duplicate ? "این را پیش‌تر گزارش کرده بودی" : "گزارشت رسید"}</b>
      <p className="muted">
        {result.duplicate
          ? "همان گزارش هنوز باز است و ادمین می‌بیندش؛ لازم نیست دوباره بفرستی."
          : "ممنون. ادمین بررسی می‌کند و اگر لازم باشد کاری می‌کند."}
        {result.blocked && ` ${name} را هم بلاک کردی.`}
      </p>
      <button className="btn" onClick={onClose}>باشه</button>
    </div>
  );
}

/**
 * «بلاک‌شده‌ها» در پروفایل: نام و عکس، و «برداشتن». فهرست با وصل‌شدن گرفته می‌شود
 * (net/safety.ts)؛ این‌جا فقط نشان داده می‌شود.
 */
export function BlockedList() {
  const rows = useSignal(blockList);
  const [busy, setBusy] = useState("");
  if (rows === null) return <div className="muted">در حال گرفتن…</div>;
  if (rows.length === 0) return <div className="muted">کسی را بلاک نکرده‌ای. از روی پیام چت یا صندلی اتاق می‌شود.</div>;
  return (
    <ul className="blocklist">
      {rows.map((r) => (
        <li key={r.id}>
          <img className="seatface" src={avatarSrc(r.avatar)} alt="" width={34} height={34} />
          <span className="nm">{r.name || "بازیکن"}</span>
          <button
            className="chip"
            disabled={busy === r.id}
            onClick={() => void (async () => {
              setBusy(r.id);
              const res = await unblockPlayer(r.id);
              setBusy("");
              if (!res.ok) notify(safetyError(res.error ?? ""));
            })()}
          >
            برداشتن
          </button>
        </li>
      ))}
    </ul>
  );
}

// ─────────────────────────── نقش‌ها ───────────────────────────

function IconMute() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 5h16v10H9l-5 4z" />
      <path d="M3 3l18 18" />
    </svg>
  );
}

function IconBlock() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M5.6 5.6l12.8 12.8" />
    </svg>
  );
}

function IconFlag() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 21V4" />
      <path d="M5 4h11l-2 4 2 4H5" />
    </svg>
  );
}

function IconCheck() {
  return (
    <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}
